import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { isEmptyValue, sortBySelectors } from "@lyfegen-platform/utils";
import { useFieldValue, useSetFieldValue } from "formik";
import { Maybe } from "graphql/jsutils/Maybe";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useAvailableBrandNames } from "../hooks/useAvailableBrandNames";
import { useClearEnrollingConditionSet } from "../hooks/useClearEnrollingConditionSet";
import { useScopeFields } from "../hooks/useScopeFields";
import { useScopes } from "../hooks/useScopes";
import { ControlState } from "../models/flexible/control-state";
import { FlexControlProps } from "../models/flexible/flex-field-controller";
import { FlexSectionController } from "../models/flexible/flex-section-controller";
import { InitialRequestConfig } from "../models/flexible/initial-request";
import { isStartingPoint } from "../models/flexible/typed-flex-entity";
import { Brand, ConditionLogicalOperator, FlexFormStatus, TreatmentSet } from "../models/generated/graphql";
import { anyNamedEntity } from "../utils/any-named-entity";
import { distinctBy } from "../utils/distinct-by";
import { renderRequiredMessage } from "../utils/render-required-message";
import { TrFun } from "../utils/types";
import { GeneralFieldController } from "./general-field-controller";
import { GET_SCOPES, whereDrugs } from "./queries";

function getSingleBrandOrEmpty(treatmentSet: TreatmentSet) {
	const brands = distinctBy(
		treatmentSet.preparations.map((p) => p.brand),
		(brand) => brand.Name
	);

	return brands.length === 1 ? brands[0] : anyNamedEntity;
}

export function getMultipleBrands(treatmentSet: TreatmentSet) {
	const brands = distinctBy(
		treatmentSet.preparations.map((p) => p.brand),
		(brand) => brand.Name
	);

	return brands;
}

function CombinedTreatmentSelectionEffect(props: { path: string; section: FlexSectionController }) {
	const { multiBrandSets: brands, indication } = useScopeFields(props.section.context);
	const [brandsField] = useFieldValue(props.path);
	const [preparations] = useFieldValue(props.section.path);
	const { selectedScopes } = useScopes(props.section.context);
	const setValue = useSetFieldValue();
	const filteredScopes = selectedScopes.filter((scope) => scope.logicalOperator === ConditionLogicalOperator.And);
	console.info({ selectedScopes });

	useEffect(() => {
		if (brandsField.length > 1 && brandsField[0] === anyNamedEntity) {
			setValue(
				props.path,
				brandsField.filter((brand: Brand) => brand.Name !== anyNamedEntity.Name)
			);
		}
		if (brandsField.length > 1 && brandsField[brandsField.length - 1] === anyNamedEntity) {
			setValue(
				props.path,
				brandsField.filter((brand: Brand) => brand.Name === anyNamedEntity.Name)
			);
		}
	}, [brandsField.length]);

	useEffect(() => {
		if (!props.section.schema.isArray || filteredScopes.length !== 1) {
			return;
		}

		if (
			props.section.schema.isArray &&
			filteredScopes.length === 1 &&
			preparations.length === 1 &&
			filteredScopes[0].treatmentSets.length > 1
		) {
			const treatmentSets = [...filteredScopes[0].treatmentSets].sort(
				sortBySelectors((treatmentSet) =>
					treatmentSet.generic.preparations.every((preparation) => preparation.BrandName !== brandsField?.Name)
				)
			);
			setValue(
				props.section.path,
				treatmentSets.map((treatmentSet, index) => ({
					[AttributeIdentifiers.BrandName]: [getSingleBrandOrEmpty(treatmentSet)],
					[AttributeIdentifiers.GenericName]: treatmentSet.generic,
				}))
			);
		}

		if (
			props.section.schema.isArray &&
			filteredScopes.length === 1 &&
			filteredScopes[0].treatmentSets.some((treatmentSet) => treatmentSet.preparations.length > 1)
		) {
			const treatmentSets = [...filteredScopes[0].treatmentSets].sort(
				sortBySelectors((treatmentSet) =>
					treatmentSet.generic.preparations.every((preparation) => preparation.BrandName !== brandsField?.Name)
				)
			);

			setValue(
				props.section.path,
				treatmentSets.map((treatmentSet, index) => ({
					[AttributeIdentifiers.Notes]:
						(preparations.length &&
							preparations.some(
								(preparation: any) =>
									!isEmptyValue(preparation?.[AttributeIdentifiers.BrandName]) &&
									!isEmptyValue(preparation?.[AttributeIdentifiers.GenericName])
							) &&
							preparations.find(
								(preparation: any) => preparation?.[AttributeIdentifiers.GenericName].Name === treatmentSet.generic.Name
							)?.notes) ||
						(preparations.length &&
							preparations.find((preparation: any) => !isEmptyValue(preparation?.[AttributeIdentifiers.Notes])))?.[
							AttributeIdentifiers.Notes
						],
					[AttributeIdentifiers.BrandName]: getMultipleBrands(treatmentSet),
					[AttributeIdentifiers.GenericName]: treatmentSet.generic,
				}))
			);
		}

		if (
			filteredScopes[0].diseases.length === 1 &&
			filteredScopes[0].enrollingConditionSets.every((conditionSet) => !conditionSet.isDiseaseNotEnrollingCondition) &&
			props.section.context.schema?.sections
				.flatMap((section) => section.fields)
				.some((field) => field.attribute.identifier === AttributeIdentifiers.Disease)
		) {
			setValue(AttributeIdentifiers.Disease, filteredScopes[0].diseases[0]);
		}

		if (
			filteredScopes[0].indications.length === 1 &&
			filteredScopes[0].enrollingConditionSets.every((conditionSet) => !conditionSet.isIndicationNotEnrollingCondition) &&
			props.section.context.schema?.sections
				.flatMap((section) => section.fields)
				.some((field) => field.attribute.identifier === AttributeIdentifiers.Indication) &&
			(!indication || !!indication.__typename)
		) {
			setValue(AttributeIdentifiers.Indication, filteredScopes[0].indications[0]);
		}
	}, [brands.length, filteredScopes?.length]);

	return null;
}

export class BrandNameFieldController extends GeneralFieldController {
	getInitialRequestComponents(): InitialRequestConfig {
		return {
			queries: [GET_SCOPES],
			variables: { where: whereDrugs(this.parent.context.entityType.id, this.parent.context.tenantId) },
		};
	}

	override useControlState(): ControlState {
		if (this.parent.context.flexEntity?.status === FlexFormStatus.Submitted && isStartingPoint(this.parent.context.flexEntity)) {
			return "enabled";
		}

		return this.parent.context.isNegotiable && !this.schema.attribute.isNegotiable ? "disabled" : "enabled";
	}

	override useControlProps(path: string): Maybe<FlexControlProps> | undefined {
		const { t } = useTranslation();
		const index = +(path.split(".").find((e) => Number.isInteger(+e)) || 0);
		const preparations = useAvailableBrandNames(this, path);
		const handleClear = useClearEnrollingConditionSet(this.parent.context);

		return {
			autoSelect: false,
			controlType: "typeahead",
			options: preparations,
			multiple: true,
			property: "Name",
			optionLabel: "Name",
			title: `${t("preparations-brandName")} ${index + 1}`,
			onClear: !this.parent.context.cases?.length && preparations.length === 1 ? handleClear : undefined,
		};
	}

	override getValueTypeProps() {
		return {
			controlType: "typeahead",
			property: "BrandName",
		};
	}

	renderBefore(path: string) {
		return this.parent.context.isEditable ? <CombinedTreatmentSelectionEffect path={path} section={this.parent} /> : <></>;
	}

	protected override createFieldValidationRuleByDataType(translation: TrFun): Yup.AnySchema {
		return Yup.array()
			.of(Yup.object().nullable().required())
			.required()
			.min(1, renderRequiredMessage(this.schema.attribute.identifier, translation));
	}

	override mapInitialValue(value: unknown): unknown {
		return value ? super.mapInitialValue(value) : [];
	}
}
